import { Injectable } from '@angular/core';
import { addressStringShort } from '@core/util/address';
import { clientLegalName } from '@core/util/client';
import { GetPrimaryTitle } from '@core/util/matter';
import { isSet } from '@core/util/value';
import { Business } from '@models/businesses';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import { BaseClient, BusinessClient, ClientType } from '@models/clients';
import {
	Matter,
	PersonMatterClient,
	BusinessMatterClient,
	MatterTitle,
} from '@models/matter';
import { AllAdjustmentsCalculated } from '@state/matter/adjustments/adjustments.selectors';
import { cloneDeep, isNumber } from 'lodash';
import { BaseFormMapper } from './base-form-mapper';
import { joinNames } from '@core/util/string';

@Injectable({
	providedIn: 'root',
})
export class ON_GST_NEW_HOUSING_REBATE_Mapper extends BaseFormMapper<ON_GST_NEW_HOUSING_REBATE> {
	CloserToFormMappings = Mappings;

	mergeFormData(
		formData: ON_GST_NEW_HOUSING_REBATE,
		matter: Matter,
		nameFormattingSettings: NameFormattingSettings,
		sellers: (PersonMatterClient | BusinessMatterClient)[],
		buyers: (PersonMatterClient | BusinessMatterClient)[],
		adjustments: AllAdjustmentsCalculated,
		business: Business,
		matterTitles: MatterTitle[],
	) {
		const _formData = formData
			? cloneDeep(formData)
			: ({} as ON_GST_NEW_HOUSING_REBATE);

		let primary = buyers?.find((x) => x.primaryContact);
		if (!primary && buyers?.length > 0) {
			primary = buyers[0];
		}

		if (!(_formData?.claimantLanguage?.length > 0)) {
			_formData.claimantLanguage = '0';
		}

		if (!(_formData?.claimantLegalName?.length > 0) && primary) {
			_formData.claimantLegalName = this.getClientsName(primary);
		}

		if (primary?.type !== ClientType.PERSON) {
			if (
				!(_formData?.claimantBusinessNumber1?.length > 0) &&
				(primary as BusinessClient)?.corporationNumber?.length >= 9
			) {
				_formData.claimantBusinessNumber1 = (
					primary as BusinessClient
				)?.corporationNumber?.substring(0, 9);
			}

			if (
				!(_formData?.claimantBusinessNumber2?.length > 0) &&
				(primary as BusinessClient)?.corporationNumber?.length >= 13
			) {
				_formData.claimantBusinessNumber2 = (
					primary as BusinessClient
				)?.corporationNumber?.substring(9, 13);
			}
		}

		if (!(_formData?.claimantContactPhone?.length > 0) && primary?.phone) {
			_formData.claimantContactPhone = primary?.phone;
		}

		if (buyers?.length > 0) {
			const others = buyers?.filter((x) => x.id !== primary?.id);

			if (others?.length > 0) {
				_formData.purchaserOtherName1 = this.getClientsName(others[0]);
			}

			if (others?.length > 1) {
				_formData.purchaseOtherName2 = this.getClientsName(others[1]);
			}
		}

		if (
			!(_formData?.purchasedAddressUnitStreet?.length > 0) &&
			matter?.propertyDetails?.address
		) {
			_formData.purchasedAddressUnitStreet = addressStringShort(
				matter?.propertyDetails?.address,
			);
		}

		if (
			!(formData?.purchasedAddressCity?.length > 0) &&
			matter?.propertyDetails?.address?.city
		) {
			_formData.purchasedAddressCity = matter?.propertyDetails?.address?.city;
		}

		if (
			!(formData?.purchasedAddressProvince?.length > 0) &&
			matter?.propertyDetails?.address?.province
		) {
			_formData.purchasedAddressProvince =
				matter?.propertyDetails?.address?.province;
		}

		if (
			!(formData?.purchasedAddressPostcode?.length > 0) &&
			matter?.propertyDetails?.address?.postcode
		) {
			_formData.purchasedAddressPostcode =
				matter?.propertyDetails?.address?.postcode?.replace(/ /g, '');
		}

		const primaryAddress =
			primary?.type === ClientType.PERSON
				? primary?.reportAddress
				: primary?.serviceAddress;
		const primaryAddress1SameAsProperty =
			!primaryAddress ||
			addressStringShort(primaryAddress) ===
				addressStringShort(matter?.propertyDetails?.address);

		if (!primaryAddress1SameAsProperty) {
			if (
				!(_formData?.claimantMailingAddressUnitStreet?.length > 0) &&
				primaryAddress
			) {
				_formData.claimantMailingAddressUnitStreet =
					addressStringShort(primaryAddress);
			}

			if (
				!(formData?.claimantMailingAddressCity?.length > 0) &&
				primaryAddress?.city
			) {
				_formData.claimantMailingAddressCity = primaryAddress?.city;
			}

			if (
				!(formData?.claimantMailingAddressProvince?.length > 0) &&
				primaryAddress?.province
			) {
				_formData.claimantMailingAddressProvince = primaryAddress?.province;
			}

			if (
				!(formData?.claimantMailingAddressPostcode?.length > 0) &&
				primaryAddress?.postcode
			) {
				_formData.claimantMailingAddressPostcode =
					primaryAddress?.postcode?.replace(/ /g, '');
			}

			if (
				!(formData?.claimantMailingAddressCountry?.length > 0) &&
				primaryAddress?.country
			) {
				_formData.claimantMailingAddressCountry = primaryAddress?.country;
			}
		}

		if (!(formData?.possessionDate?.length > 0) && matter?.closingDate) {
			_formData.possessionDate = matter?.closingDate?.replace(/-/g, '');
		}

		const primaryPropertyTitle = GetPrimaryTitle(matterTitles);

		if (
			!(formData?.lot?.length > 0) &&
			primaryPropertyTitle &&
			primaryPropertyTitle[1]?.lot
		) {
			_formData.lot = primaryPropertyTitle[1]?.lot;
		}

		if (
			!(formData?.plan?.length > 0) &&
			primaryPropertyTitle &&
			primaryPropertyTitle[1]?.plan
		) {
			_formData.plan = primaryPropertyTitle[1]?.plan;
		}

		if (
			!(formData?.other?.length > 0) &&
			primaryPropertyTitle &&
			(primaryPropertyTitle[1]?.section || primaryPropertyTitle[1]?.parcel)
		) {
			const propertyOther = [];
			if (primaryPropertyTitle[1]?.section) {
				propertyOther.push(primaryPropertyTitle[1]?.section);
			}
			if (primaryPropertyTitle[1]?.parcel) {
				propertyOther.push(primaryPropertyTitle[1]?.parcel);
			}
			_formData.other = propertyOther.join(',');
		}

		const builder = sellers?.find(
			(x) => x.type === ClientType.BUILDER || x.type === ClientType.CORPORATION,
		);

		if (!isSet(_formData?.builderLegalName)) {
			if (builder) {
				_formData.builderLegalName = (builder as BusinessClient).name;
			} else {
				const names = sellers?.map((x) =>
					clientLegalName(x, nameFormattingSettings, {
						applyFormatting: false,
					}),
				);
				const fieldValue = joinNames(names);
				_formData.builderLegalName = fieldValue;
			}
		}

		if (
			!(
				isSet(_formData?.builderBusinessNumber1) ||
				isSet(_formData?.builderBusinessNumber2)
			)
		) {
			_formData.builderBusinessNumber1 = (
				builder as BusinessClient
			)?.corporationNumber?.substring(0, 9);
			_formData.claimantBusinessNumber2 = (
				builder as BusinessClient
			)?.corporationNumber?.substring(9, 13);
		}

		const seller = builder ? builder : sellers?.length > 0 ? sellers[0] : null;
		const sellersAddress = getAddress(seller);

		if (sellersAddress) {
			if (!isSet(_formData?.builderAddressUnitStreet)) {
				_formData.builderAddressUnitStreet = addressStringShort(sellersAddress);
			}

			if (!isSet(_formData?.builderAddressCity) && sellersAddress?.city) {
				_formData.builderAddressCity = sellersAddress?.city;
			}

			if (!isSet(_formData?.builderAddressProvince) && sellersAddress?.city) {
				_formData.builderAddressProvince =
					matter?.propertyDetails?.address?.province;
			}

			if (
				!isSet(formData?.purchasedAddressPostcode) &&
				sellersAddress?.postcode
			) {
				_formData.builderAddressPostcode =
					matter?.propertyDetails?.address?.postcode?.replace(/ /g, '');
			}

			if (!isSet(formData?.builderAddressCountry) && sellersAddress?.country) {
				_formData.builderAddressCountry = sellersAddress.country;
			}
		}

		if (!isSet(_formData?.builderAddressTel) && isSet(seller?.phone)) {
			_formData.builderAddressTel = seller?.phone;
		}

		const numberSpaceCount = 6;

		if (
			!(_formData?.rebateCalcA?.length > 0) &&
			adjustments?.salePriceCalculated?.hstFed
		) {
			_formData.rebateCalcA = this.toFormDollarPrice(
				adjustments.salePriceCalculated.hstFed,
				numberSpaceCount,
			);
		}

		if (
			!(formData?.rebateCalcB?.length > 0) &&
			adjustments?.salePriceCalculated?.nsp
		) {
			_formData.rebateCalcB = this.toFormDollarPrice(
				adjustments.salePriceCalculated.nsp,
				numberSpaceCount,
			);
		}

		if (
			!isSet(_formData?.rebateCalcC) &&
			isSet(adjustments?.salePriceCalculated?.hstRebateFed)
		) {
			_formData.rebateCalcC = this.toFormDollarPrice(
				adjustments.salePriceCalculated.hstRebateFed,
				numberSpaceCount,
			);
		}

		if (
			!isSet(_formData?.rebateCalcD) &&
			isSet(adjustments?.salePriceCalculated?.hstRebateProv)
		) {
			_formData.rebateCalcD = this.toFormDollarPrice(
				adjustments.salePriceCalculated.hstRebateProv,
				numberSpaceCount,
			);
		}

		if (
			!isSet(_formData?.rebateCalcE) &&
			isSet(_formData?.rebateCalcC) &&
			isSet(_formData?.rebateCalcD)
		) {
			const formC = this.formValueToCents(_formData?.rebateCalcC);
			const formD = this.formValueToCents(_formData?.rebateCalcD);

			if (isNumber(formC) && isNumber(formD)) {
				_formData.rebateCalcE = this.toFormDollarPrice(
					formC + formD,
					numberSpaceCount,
				);
			}
		}

		return _formData;
	}

	MapValues(): Partial<ON_GST_NEW_HOUSING_REBATE> {
		return {};
	}
}

const getAddress = (client: BaseClient) =>
	client?.reportAddress ?? client?.serviceAddress;

const Mappings: ON_GST_NEW_HOUSING_REBATE = {
	claimantLegalName: 'form1[0].Page1[0].PartA[0].ClaimantName[0].NameField[0]',
	claimantBusinessNumber1:
		'form1[0].Page1[0].PartA[0].BusinessNumber[0].BusinessNumber[0].BusinessNumber_RT1[0]',
	claimantBusinessNumber2:
		'form1[0].Page1[0].PartA[0].BusinessNumber[0].BusinessNumber[0].BusinessNumber_RT2[0]',
	claimantContactPhone:
		'form1[0].Page1[0].PartA[0].DayPhoneNumber[0].DaytimePhoneNumber[0]',
	claimantContactPhoneExt:
		'form1[0].Page1[0].PartA[0].DayPhoneNumber[0].Phone_Ext[0]',
	claimantHomePhone: 'form1[0].Page1[0].PartA[0].HomePhoneNumber[0]',
	claimantLanguage:
		'form1[0].Page1[0].PartA[0].LanguagePref[0].RadioButtonList1[0]',
	purchaserOtherName1:
		'form1[0].Page2[0].PartA_bis[0].OtherPurchaser[0].OtherPurchaser1[0].NameField[0]',
	purchaseOtherName2:
		'form1[0].Page2[0].PartA_bis[0].OtherPurchaser[0].OtherPurchaser2[0].NameField[0]',
	purchasedAddressUnitStreet:
		'form1[0].Page2[0].PartA_bis[0].HouseAddress[0].PhysicalAddress[0]',
	purchasedAddressCity:
		'form1[0].Page2[0].PartA_bis[0].HouseAddress[0].City[0]',
	purchasedAddressProvince:
		'form1[0].Page2[0].PartA_bis[0].HouseAddress[0].Province[0]',
	purchasedAddressPostcode:
		'form1[0].Page2[0].PartA_bis[0].HouseAddress[0].PostalCode[0]',
	claimantMailingAddressUnitStreet:
		'form1[0].Page2[0].PartA_bis[0].MailingAddress[0].BusinessAddress[0]',
	claimantMailingAddressCity:
		'form1[0].Page2[0].PartA_bis[0].MailingAddress[0].City[0]',
	claimantMailingAddressProvince:
		'form1[0].Page2[0].PartA_bis[0].MailingAddress[0].ProvState_DropDown[0]',
	claimantMailingAddressPostcode:
		'form1[0].Page2[0].PartA_bis[0].MailingAddress[0].PostalCode_ZIP[0]',
	claimantMailingAddressCountry:
		'form1[0].Page2[0].PartA_bis[0].MailingAddress[0].Country[0]',
	purchasePrimaryRes:
		'form1[0].Page2[0].PartB[0].MainResidence_Yes_No[0].RadioButtonList2[0]',
	purchaseAgreementDate:
		'form1[0].Page2[0].PartB[0].PurchaseDate[0].PurchaseAgreementDate[0].DateYYYYMMDD_Comb[0]',
	ownershipTransferDate:
		'form1[0].Page2[0].PartB[0].TransferDate[0].OwnershipDate[0].DateYYYYMMDD_Comb[0]',
	possessionDate:
		'form1[0].Page2[0].PartB[0].OwnershipTransfered[0].DateYYYYMMDD_Comb[0].DateYYYYMMDD_Comb[1]',
	lot: 'form1[0].Page3[0].PartB_continued[0].LegalDescriptionSF[0].BusinessAddress[0]',
	plan: 'form1[0].Page3[0].PartB_continued[0].LegalDescriptionSF[0].PlanNumber[0]',
	other: 'form1[0].Page3[0].PartB_continued[0].LegalDescriptionSF[0].Other[0]',
	mobileHomeManufacturer:
		'form1[0].Page3[0].PartB_continued[0].LegalDescriptionSF[0].Manufacturer[0]',
	mobileHomeModel:
		'form1[0].Page3[0].PartB_continued[0].LegalDescriptionSF[0].Model[0]',
	mobileHomeSerialMumber:
		'form1[0].Page3[0].PartB_continued[0].LegalDescriptionSF[0].SerialNumber[0]',
	housingType: 'form1[0].Page3[0].PartC[0].HousingType[0].RadioButtonGroup[0]',
	builderRebateFiled1A:
		'form1[0].Page3[0].PartC[0].Type1A[0].SectionC_ApplicationType[0]',
	builderRebateFiled1B:
		'form1[0].Page3[0].PartC[0].Type1B[0].SectionC_ApplicationType[0]',
	directFiledRates2:
		'form1[0].Page4[0].PartC_bis[0].Type2[0].SectionC_ApplicationType[0]',
	directFiledRates3:
		'form1[0].Page4[0].PartC_bis[0].Type3[0].SectionC_ApplicationType[0]',
	directFiledRates5:
		'form1[0].Page4[0].PartC_bis[0].Type5[0].SectionC_ApplicationType[0]',
	builderLegalName: 'form1[0].Page4[0].PartD[0].LegalName[0].Name[0]',
	builderBusinessNumber1:
		'form1[0].Page4[0].PartD[0].BusinessNumber[0].BusinessNumber[0].BusinessNumber_RT1[0]',
	builderBusinessNumber2:
		'form1[0].Page4[0].PartD[0].BusinessNumber[0].BusinessNumber[0].BusinessNumber_RT2[0]',
	builderAddressUnitStreet:
		'form1[0].Page4[0].PartD[0].BuilderAddress[0].Address[0]',
	builderAddressCity: 'form1[0].Page4[0].PartD[0].BuilderAddress[0].City[0]',
	builderAddressProvince:
		'form1[0].Page4[0].PartD[0].BuilderAddress[0].ProvinceTerritoryState_DropDown[0]',
	builderAddressPostcode:
		'form1[0].Page4[0].PartD[0].BuilderAddress[0].International_PostalCode_ZIP[0]',
	builderAddressCountry:
		'form1[0].Page4[0].PartD[0].BuilderAddress[0].Country[0]',
	builderAddressTel:
		'form1[0].Page4[0].PartD[0].BuilderAddress[0].Inter…tionalPhoneNumber[0].International_PhoneNumber[0]',
	builderAddressTelExt:
		'form1[0].Page4[0].PartD[0].BuilderAddress[0].InternationalPhoneNumber[0].Extension[0]',
	builderPayRebateDirect: 'form1[0].Page4[0].RefoundPayedDirectly[0].YesNo[0]',
	builderRebateFromDate:
		'form1[0].Page4[0].DateFromTo[0].FromToDates_Comb_Adv_EN[0].FromDate[0]',
	builderRebateToDate:
		'form1[0].Page4[0].DateFromTo[0].FromToDates_Comb_Adv_EN[0].ToDate[0]',
	builderNamePrint: 'form1[0].Page4[0].Name[0]',
	builderSignatureDate: 'form1[0].Page4[0].Date[0].DateYYYYMMDD_Comb[0]',
	claimantCertificationNamePrint: 'form1[0].Page5[0].PartE[0].Name[0]',
	claimantCertificationSignatureDate:
		'form1[0].Page5[0].PartE[0].Date[0].DateYYYYMMDD_Comb[0]',
	rebateCalcA:
		'form1[0].Page5[0].PartF_Section1[0].LineA[0].Numeric_Group[0].Amount[0]',
	rebateCalcB:
		'form1[0].Page5[0].PartF_Section1[0].LineB[0].Numeric_Group[0].Amount[0]',
	rebateCalcC:
		'form1[0].Page5[0].PartF_Section1[0].LineC[0].Numeric_Group[0].Amount[0]',
	rebateCalcD:
		'form1[0].Page5[0].PartF_Section1[0].LineD[0].Numeric_Group[0].Amount[0]',
	rebateCalcE: 'form1[0].Page5[0].PartF_Section1[0].Amount[0]',
	rebateCalcF:
		'form1[0].Page6[0].PartF_Section2[0].LineF[0].Numeric_Group[0].Amount[0]',
	rebateCalcG:
		'form1[0].Page6[0].PartF_Section2[0].LineG[0].Numeric_Group[0].Amount[0]',
	rebateCalcH:
		'form1[0].Page6[0].PartF_Section2[0].LineH[0].Numeric_Group[0].Amount[0]',
	rebateCalcI:
		'form1[0].Page6[0].PartF_Section2[0].LineI[0].Numeric_Group[0].Amount[0]',
	rebateCalcJ:
		'form1[0].Page6[0].PartF_Section2[0].LineJ[0].Numeric_Group[0].Amount[0]',
	rebateCalcK:
		'form1[0].Page6[0].PartF_Section3[0].LineK[0].Numeric_Group[0].Amount[0]',
	rebateCalcL:
		'form1[0].Page6[0].PartF_Section3[0].LineL[0].Numeric_Group[0].Amount[0]',
	rebateCalcM:
		'form1[0].Page6[0].PartF_Section3[0].LineM[0].Numeric_Group[0].Amount[0]',
	rebateCalcN:
		'form1[0].Page6[0].PartF_Section3[0].LineN[0].Numeric_Group[0].Amount[0]',
	branchNumber: 'form1[0].Page6[0].PartG[0].PartG_BranchNo_Comb[0].BranchNo[0]',
	institutionNumber:
		'form1[0].Page6[0].PartG[0].PartG_InstNo_Comb[0].InstNo[0]',
	accountNumber: 'form1[0].Page6[0].PartG[0].PartG_AcctNo_Comb[0].AcctNo[0]',
	accountHolder: 'form1[0].Page6[0].PartG[0].PartG_AccountName[0].Name[0]',
};

export interface ON_GST_NEW_HOUSING_REBATE {
	claimantLegalName: string;
	claimantBusinessNumber1: string;
	claimantBusinessNumber2: string;
	claimantContactPhone: string;
	claimantContactPhoneExt: string;
	claimantHomePhone: string;
	claimantLanguage: string;
	purchaserOtherName1: string;
	purchaseOtherName2: string;
	purchasedAddressUnitStreet: string;
	purchasedAddressCity: string;
	purchasedAddressProvince: string;
	purchasedAddressPostcode: string;
	claimantMailingAddressUnitStreet: string;
	claimantMailingAddressCity: string;
	claimantMailingAddressProvince: string;
	claimantMailingAddressPostcode: string;
	claimantMailingAddressCountry: string;
	purchasePrimaryRes: string;
	purchaseAgreementDate: string;
	ownershipTransferDate: string;
	possessionDate: string;
	lot: string;
	plan: string;
	other: string;
	mobileHomeManufacturer: string;
	mobileHomeModel: string;
	mobileHomeSerialMumber: string;
	housingType: string;
	builderRebateFiled1A: string;
	builderRebateFiled1B: string;
	directFiledRates2: string;
	directFiledRates3: string;
	directFiledRates5: string;
	builderLegalName: string;
	builderBusinessNumber1: string;
	builderBusinessNumber2: string;
	builderAddressUnitStreet: string;
	builderAddressCity: string;
	builderAddressProvince: string;
	builderAddressPostcode: string;
	builderAddressCountry: string;
	builderAddressTel: string;
	builderAddressTelExt: string;
	builderPayRebateDirect: string;
	builderRebateFromDate: string;
	builderRebateToDate: string;
	builderNamePrint: string;
	builderSignatureDate: string;
	claimantCertificationNamePrint: string;
	claimantCertificationSignatureDate: string;
	rebateCalcA: string;
	rebateCalcB: string;
	rebateCalcC: string;
	rebateCalcD: string;
	rebateCalcE: string;
	rebateCalcF: string;
	rebateCalcG: string;
	rebateCalcH: string;
	rebateCalcI: string;
	rebateCalcJ: string;
	rebateCalcK: string;
	rebateCalcL: string;
	rebateCalcM: string;
	rebateCalcN: string;
	branchNumber: string;
	institutionNumber: string;
	accountNumber: string;
	accountHolder: string;
}
